import styled, { keyframes } from 'styled-components';

interface SignalsNavItemProps {
  variant: 'desktop' | 'mobile';
  status: 'enabled' | 'coming_soon';
  isSelected: boolean;
  onClick: () => void;
}

// "Signals" entry that lives in the main navbar (leftmost) and the mobile
// sidebar. For merchants without the feature flag, the entry still
// appears so the feature is visible-as-aspiration — but it carries a
// soft "Coming soon" pill and routes to a placeholder page rather than
// the live dashboard. This is intentional per the Phase 1 plan: we want
// merchants to see what's coming, not to surface a missing route.

export const SignalsNavItem = ({
  variant,
  status,
  isSelected,
  onClick
}: SignalsNavItemProps) => {
  const isComingSoon = status === 'coming_soon';

  if (variant === 'desktop') {
    return (
      <DesktopWrapper
        type="button"
        onClick={onClick}
        className={isSelected ? 'selected' : ''}
        aria-label={isComingSoon ? 'Sapaad Signals — coming soon' : 'Sapaad Signals'}
        aria-current={isSelected ? 'page' : undefined}
      >
        <DesktopIcon $isSelected={isSelected} aria-hidden="true">
          <SignalGlyph isSelected={isSelected} />
        </DesktopIcon>
        <DesktopLabel>Signals</DesktopLabel>
        {isComingSoon && <DesktopBadge>Coming soon</DesktopBadge>}
      </DesktopWrapper>
    );
  }

  return (
    <MobileWrapper
      type="button"
      onClick={onClick}
      className="nav-item-container"
      aria-current={isSelected ? 'page' : undefined}
    >
      <div className="nav-item-container">
        <div className="nav-item-content">
          <div className="nav-item-icon">
            <SignalGlyph isSelected={isSelected} />
          </div>
          <div className="nav-item-text">
            <div className="nav-item-title">Signals</div>
            {isComingSoon && <MobileBadge>Coming soon</MobileBadge>}
          </div>
        </div>
        {isSelected && <div className="nav-item-indicator" />}
      </div>
    </MobileWrapper>
  );
};

// Custom SVG: three concentric arcs forming a signal/wave glyph. Picks up
// the green selection accent that matches the rest of the navbar.
const SignalGlyph = ({ isSelected }: { isSelected: boolean }) => {
  const fill = isSelected ? '#0d8a6a' : 'white';
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="20" r="2.2" fill={fill} />
      <path
        d="M9 17.4c1.3-1.3 3.1-2.1 5-2.1s3.7.8 5 2.1"
        stroke={fill}
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M5.8 14.2c2.2-2.2 5.1-3.4 8.2-3.4s6 1.2 8.2 3.4"
        stroke={fill}
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M2.6 11c3-3 7-4.7 11.4-4.7S22.4 8 25.4 11"
        stroke={fill}
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.6"
        fill="none"
      />
    </svg>
  );
};

// ── Animations ────────────────────────────────────────────────────────────

const softPulse = keyframes`
  0%, 100% { opacity: 0.85; }
  50%      { opacity: 1; }
`;

// ── Styled components ─────────────────────────────────────────────────────

const DesktopWrapper = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 14px;
  background: transparent;
  border: none;
  border-right: 1px solid rgb(73, 76, 82);
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #c9c9c9;
  }

  &.selected {
    color: #0d8a6a;
  }
`;

const DesktopIcon = styled.span<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DesktopLabel = styled.span`
  font-weight: 600;
`;

const DesktopBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #534ab7 0%, #1d9e75 200%);
  color: #fff;
  white-space: nowrap;
  margin-left: 2px;
  animation: ${softPulse} 3s ease-in-out infinite;
  box-shadow: 0 1px 4px rgba(83, 74, 183, 0.4);
`;

const MobileWrapper = styled.button`
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  padding: 14px 20px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  position: relative;
`;

const MobileBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 999px;
  background: linear-gradient(135deg, #534ab7 0%, #1d9e75 200%);
  color: #fff;
`;
